import { Type } from '@angular/core';
import { SvcSearchComponent } from '../svc-search/svc-search.component';
import { SvcRequestComponent } from '../svc-request/svc-request.component';
import { UpdateMyScannerBoardComponent } from '../update-my-scanner-board/update-my-scanner-board.component';
import { OtherOfficeStockProcessComponent } from '../other-office-stock-process/other-office-stock-process.component';
import { BatchpartystockuploaderComponent } from '../batchpartystockuploader/batchpartystockuploader.component';

export type ActionLoader = () => Promise<{ component: Type<any> }>;

/** Collapses the differences that stop a hand-entered DB name matching a map key. */
const normalizeActionName = (name: string): string =>
  name.trim().replace(/\s+/g, ' ').toLowerCase();

/**
 * Resolves an action name to its component loader.
 *
 * Action names live in the `action` DB table and are typed by hand in the
 * Manage Actions screen, while the keys below are typed by hand in this file.
 * An exact-match lookup therefore fails on a trailing space, a double space or
 * a casing difference — none of which are visible in a console log. Try the
 * exact key first, then fall back to a normalized comparison.
 *
 * `matchedKey` is returned so the caller can warn when the fallback fired:
 * the DB row still wants fixing, it just no longer breaks the screen.
 */
export function resolveActionLoader(
  actionName: string,
): { loader: ActionLoader; matchedKey: string } | null {
  if (!actionName) return null;

  const exact = ACTION_MODULE_MAP[actionName];
  if (exact) return { loader: exact, matchedKey: actionName };

  const target = normalizeActionName(actionName);
  const matchedKey = Object.keys(ACTION_MODULE_MAP).find(
    (key) => normalizeActionName(key) === target,
  );

  return matchedKey ? { loader: ACTION_MODULE_MAP[matchedKey], matchedKey } : null;
}

export const ACTION_MODULE_MAP: Record<string, ActionLoader> = {
  Search: () =>
    import('../../components/actions/elastic-search/elastic-search.component').then(
      (m) => ({ component: m.ElasticSearchComponent }),
    ),

  Roles: () =>
    import('../../components/actions/roles/roles.component').then((m) => ({
      component: m.RolesComponent,
    })),

  Inward: () =>
    import('../../components/actions/stone-inward/stone-inward.component').then(
      (m) => ({ component: m.StoneInwardComponent }),
    ),

  'Manage Actions': () =>
    import('../../components/actions/manage-action/manage-action.component').then(
      (m) => ({ component: m.ManageActionComponent }),
    ),

  Users: () =>
    import('../../components/actions/user/user.component').then((m) => ({
      component: m.UserComponent,
    })),

  'Parameter Master': () =>
    import('../../components/actions/master/master.component').then((m) => ({
      component: m.MasterComponent,
    })),

  Upload: () =>
    import('../../components/actions/upload-stone/upload-stone.component').then(
      (m) => ({ component: m.UploadStoneComponent }),
    ),

  Assortment: () =>
    import('../../components/actions/assortment/assortment.component').then(
      (m) => ({ component: m.AssortmentComponent }),
    ),

  'Inventory Manager': () =>
    import('../../components/actions/inventory-manager/inventory-manager.component').then(
      (m) => ({ component: m.InventoryManagerComponent }),
    ),

  'Box Master': () =>
    import('../../components/actions/box-master/box-master.component').then(
      (m) => ({ component: m.BoxMasterComponent }),
    ),

  'Return Assortment Error Stones': () =>
    import('../../components/actions/transfer-to-manufacture/transfer-to-manufacture.component').then(
      (m) => ({ component: m.TransferToManufactureComponent }),
    ),

  Inventory: () =>
    import('../../components/actions/inventory/inventory.component').then(
      (m) => ({ component: m.InventoryComponent }),
    ),

  Sort: () =>
    import('../../components/actions/sort/sort.component').then((m) => ({
      component: m.SortComponent,
    })),

  'SSPD Parameter Config': () =>
    import('../../components/actions/ss-parameter/ss-parameter.component').then(
      (m) => ({ component: m.SSParameterComponent }),
    ),

  Certification: () =>
    import('../../components/actions/certification/certification.component').then(
      (m) => ({ component: m.CertificationComponent }),
    ),

  'Manage Voucher Book': () =>
    import('../../components/actions/voucher-book/voucher-book.component').then(
      (m) => ({ component: m.VoucherBookComponent }),
    ),

  'Party Master': () =>
    import('../../components/actions/customer/customer.component').then(
      (m) => ({ component: m.CustomerComponent }),
    ),

  Consignment: () =>
    import('../../components/actions/consignment/consignment.component').then(
      (m) => ({ component: m.ConsignmentComponent }),
    ),

  'Price List Uploader': () =>
    import('../../components/actions/price-list-uploader/price-list-uploader.component').then(
      (m) => ({ component: m.PriceListUploaderComponent }),
    ),

  'Mix Transfer To Sales': () =>
    import('../../components/actions/transfer-to-sales-mix/transfer-to-sales-mix.component').then(
      (m) => ({ component: m.TransferToSalesMixComponent }),
    ),

  'Single Transfer To Sales': () =>
    import('../../components/actions/transfer-to-sales-single/transfer-to-sales-single.component').then(
      (m) => ({ component: m.TransferToSalesSingleComponent }),
    ),

  'Pricing Policy': () =>
    import('../../components/actions/pricing-policy/pricing-policy.component').then(
      (m) => ({ component: m.PricingPolicy }),
    ),

  'Assort Parameter Config': () =>
    import('../../components/actions/assort-parameter-config/assort-parameter-config.component').then(
      (m) => ({ component: m.AssortParameterConfigComponent }),
    ),

  'Single Search': () =>
    import('../../components/actions/search-single-stone/search-single-stone.component').then(
      (m) => ({ component: m.SearchSingleStoneComponent }),
    ),

  'Mix Search': () =>
    import('../../components/actions/search-mix-stone/search-mix-stone.component').then(
      (m) => ({ component: m.SearchMixStoneComponent }),
    ),

  'SVC Single Search': () =>
    import('../../components/svc-search/svc-search.component').then((m) => ({
      component: m.SvcSearchComponent,
    })),
  'SVC Request List': () =>
    import('../../components/svc-request/svc-request.component').then((m) => ({
      component: m.SvcRequestComponent,
    })),
  'Single With Me': () =>
    import('../../components/actions/stone-with-me-single/stone-with-me-single.component').then(
      (m) => ({ component: m.StoneWithMeSingleComponent }),
    ),

  'Mix With Me': () =>
    import('../../components/actions/stone-with-me-mix/stone-with-me-mix.component').then(
      (m) => ({ component: m.StoneWithMeMixComponent }),
    ),

  'Apply Pricing Policy': () =>
    import('../../components/actions/apply-policy/apply-policy.component').then(
      (m) => ({ component: m.ApplyPolicyComponent }),
    ),

  'Assortment Stock Search': () =>
    import('../../components/actions/assortment-search/assortment-search.component').then(
      (m) => ({ component: m.AssortmentSearchComponent }),
    ),

  'Clear Assortment Stock': () =>
    import('../../components/actions/clear-assortment-stock/clear-assortment-stock.component').then(
      (m) => ({ component: m.ClearAssortmentStockComponent }),
    ),

  'Export Templates': () =>
    import('../../components/actions/export-templates/export-templates.component').then(
      (m) => ({ component: m.ExportTemplatesComponent }),
    ),

  'With Me': () =>
    import('../../components/actions/stones-with-me/stones-with-me.component').then(
      (m) => ({ component: m.StonesWithMeComponent }),
    ),

  Program: () =>
    import('../../components/actions/programme/programme.component').then(
      (m) => ({ component: m.ProgrammeComponent }),
    ),

  'Default Export Template': () =>
    import('../../components/actions/default-templates/default-templates.component').then(
      (m) => ({ component: m.DefaultTemplatesComponent }),
    ),

  'Pricing Policy Tracker': () =>
    import('../../components/actions/policy-tracker/policy-tracker.component').then(
      (m) => ({ component: m.PolicyTrackerComponent }),
    ),

  PD: () =>
    import('../../components/actions/pd/pd.component').then((m) => ({
      component: m.PDComponent,
    })),

  'Status Master': () =>
    import('../../components/actions/status-master/status-master.component').then(
      (m) => ({ component: m.StatusMasterComponent }),
    ),

  'Lab Parameter Mapping': () =>
    import('../../components/actions/parameter-mapping-master/parameter-mapping-master.component').then(
      (m) => ({ component: m.ParameterMappingMasterComponent }),
    ),

  'Update Parameter': () =>
    import('../../components/actions/update-parameter/update-parameter.component').then(
      (m) => ({ component: m.UpdateParameterComponent }),
    ),

  'Memo Client': () =>
    import('../../components/actions/memo-client/memo-client.component').then(
      (m) => ({ component: m.MemoClientComponent }),
    ),

  'Customer Confirm': () =>
    import('../../components/actions/customer-confirm/customer-confirm.component').then(
      (m) => ({ component: m.CustomerConfirmComponent }),
    ),

  'Consignment Confirm': () =>
    import('../../components/actions/consignment-confirm/consignment-confirm.component').then(
      (m) => ({ component: m.ConsignmentConfirmComponent }),
    ),

  'Consignment In Stock': () =>
    import('../../components/actions/consignment-in-stock/consignment-in-stock.component').then(
      (m) => ({ component: m.ConsignmentInStockComponent }),
    ),

  'Memo Service': () =>
    import('../../components/actions/memo-service/memo-service.component').then(
      (m) => ({ component: m.MemoServiceComponent }),
    ),

  'Memo Inward': () =>
    import('../../components/actions/memo-inward/memo-inward.component').then(
      (m) => ({ component: m.MemoInwardComponent }),
    ),

  'Lab Expenses': () =>
    import('../../components/actions/lab-expenses/lab-expenses.component').then(
      (m) => ({ component: m.LabExpensesComponent }),
    ),

  'External Inventory': () =>
    import('../../components/actions/external-inventory/external-inventory.component').then(
      (m) => ({ component: m.ExternalInventoryComponent }),
    ),

  Recut: () =>
    import('../../components/actions/recut/recut.component').then((m) => ({
      component: m.RecutComponent,
    })),

  Observation: () =>
    import('../../components/actions/observation/observation.component').then(
      (m) => ({ component: m.ObservationComponent }),
    ),

  'Mix Size Master': () =>
    import('../../components/actions/pricing-size-master/pricing-size-master.component').then(
      (m) => ({ component: m.PricingSizeMasterComponent }),
    ),

  'Forex Rate Updater': () =>
    import('../../components/actions/conversion-rate-master/conversion-rate-master.component').then(
      (m) => ({ component: m.ConversionRateMasterComponent }),
    ),

  'Lab Forex Rate Updater': () =>
    import('../../components/actions/lab-conversion-rate-master/lab-conversion-rate-master.component').then(
      (m) => ({ component: m.LabConversionRateMasterComponent }),
    ),

  Company: () =>
    import('../../components/actions/company/company.component').then((m) => ({
      component: m.CompanyComponent,
    })),

  'Update RFID': () =>
    import('../../components/actions/update-rfid/update-rfid.component').then(
      (m) => ({ component: m.UpdateRfidComponent }),
    ),

  'Inter Cssp Transfer': () =>
    import('../../components/actions/inter-cssptransfer/inter-cssptransfer.component').then(
      (m) => ({ component: m.InterCSSPTransferComponent }),
    ),

  'Audit CSSP': () =>
    import('../../components/actions/audit-cssp/audit-cssp.component').then(
      (m) => ({ component: m.AuditCSSPComponent }),
    ),

  'Inter Box Transfer': () =>
    import('../../components/actions/box-transfer/box-transfer.component').then(
      (m) => ({ component: m.BoxTransferComponent }),
    ),

  'Single Mixing': () =>
    import('../../components/actions/single-to-mix/single-to-mix.component').then(
      (m) => ({ component: m.SingleToMixComponent }),
    ),

  'Single To Mix Transfer': () =>
    import('../../components/actions/single-to-mix-transfer/single-to-mix-transfer.component').then(
      (m) => ({ component: m.SingleToMixTransferComponent }),
    ),

  'Single Transfer To Assortment': () =>
    import('../../components/actions/mix-to-single-transfer/mix-to-single-transfer.component').then(
      (m) => ({ component: m.MixToSingleTransferComponent }),
    ),

  'Mixing History': () =>
    import('../../components/actions/purchase-history/purchase-history.component').then(
      (m) => ({ component: m.PurchaseHistoryComponent }),
    ),

  'RAPAPORT Mapping': () =>
    import('../../components/actions/rapaport-mapping/rapaport-mapping.component').then(
      (m) => ({ component: m.RapaportMappingComponent }),
    ),

  'Term Master': () =>
    import('../../components/actions/term-master/term-master.component').then(
      (m) => ({ component: m.TermMasterComponent }),
    ),

  'RAPAPORT Price List Uploader': () =>
    import('../../components/actions/rapaport-price-list-uploader/rapaport-price-list-uploader.component').then(
      (m) => ({ component: m.RapaportPriceListUploaderComponent }),
    ),

  'Pricing Policy Chain': () =>
    import('../../components/actions/policy-chain/policy-chain.component').then(
      (m) => ({ component: m.PolicyChainComponent }),
    ),

  'Mix To Single': () =>
    import('../../components/actions/mix-to-single/mix-to-single.component').then(
      (m) => ({ component: m.MixToSingleComponent }),
    ),

  'Purchase To Single': () =>
    import('../../components/actions/purchase-to-single/purchase-to-single.component').then(
      (m) => ({ component: m.PurchaseToSingleComponent }),
    ),

  'Purchase Inward': () =>
    import('../../components/actions/purchase-inward/purchase-inward.component').then(
      (m) => ({ component: m.PurchaseInwardComponent }),
    ),

  'Mix Sales Inventory': () =>
    import('../../components/actions/sales-inventory/sales-inventory.component').then(
      (m) => ({ component: m.SalesInventoryComponent }),
    ),

  'Purchase To Mix': () =>
    import('../../components/actions/purchase-to-mix/purchase-to-mix.component').then(
      (m) => ({ component: m.PurchaseToMixComponent }),
    ),

  'Update StoneId': () =>
    import('../../components/actions/update-stone-id/update-stone-id.component').then(
      (m) => ({ component: m.UpdateStoneIdComponent }),
    ),

  'Update Stone Cts': () =>
    import('../../components/actions/update-stone-cts/update-stone-cts.component').then(
      (m) => ({ component: m.UpdateStoneCtsComponent }),
    ),

  'Revert Recut Return': () =>
    import('../../components/actions/recut-return-revert/recut-return-revert.component').then(
      (m) => ({ component: m.RecutReturnRevertComponent }),
    ),

  'Inventory Manager SS': () =>
    import('../../components/actions/inventory-manager-ss/inventory-manager-ss.component').then(
      (m) => ({ component: m.InventoryManagerSSComponent }),
    ),

  'Inventory Manager MIX': () =>
    import('../../components/actions/inventory-manager-mix/inventory-manager-mix.component').then(
      (m) => ({ component: m.InventoryManagerMIXComponent }),
    ),

  'Update Barcode': () =>
    import('../../components/actions/update-barcode/update-barcode.component').then(
      (m) => ({ component: m.UpdateBarcodeComponent }),
    ),

  'Customer Hold': () =>
    import('../../components/actions/customer-hold/customer-hold.component').then(
      (m) => ({ component: m.CustomerHoldComponent }),
    ),

  'Lab Services': () =>
    import('../../components/actions/lab-services/lab-services.component').then(
      (m) => ({ component: m.LabServicesComponent }),
    ),

  'Cssp Transfer Mapping': () =>
    import('../../components/actions/cssp-transfer-mapping/cssp-transfer-mapping.component').then(
      (m) => ({ component: m.CsspTransferMappingComponent }),
    ),

  'Single Mix Parameter Mapping': () =>
    import('../../components/actions/single-mix-param-mapping/single-mix-param-mapping.component').then(
      (m) => ({ component: m.SingleMixParamMappingComponent }),
    ),

  'Pending Transfer Approval': () =>
    import('../../components/actions/pending-approval/pending-approval.component').then(
      (m) => ({ component: m.PendingApprovalComponent }),
    ),

  'Request Approval': () =>
    import('../../components/actions/request-approval-of-out-platform/request-approval-of-out-platform.component').then(
      (m) => ({ component: m.RequestApprovalOfOutPlatformComponent }),
    ),

  'Website Customer Approval': () =>
    import('../../components/actions/update-kycapproval/update-kycapproval.component').then(
      (m) => ({ component: m.UpdateKYCApprovalComponent }),
    ),

  'Website Offer': () =>
    import('../../components/actions/website-offer/website-offer.component').then(
      (m) => ({ component: m.WebsiteOfferComponent }),
    ),

  Reports: () =>
    import('../../components/actions/reports/reports.component').then((m) => ({
      component: m.ReportsComponent,
    })),

  'Recut Inward': () =>
    import('../../components/actions/recut-inward/recut-inward.component').then(
      (m) => ({ component: m.RecutInwardComponent }),
    ),

  'Stone Finalization': () =>
    import('../../components/actions/stone-finalization/stone-finalization.component').then(
      (m) => ({ component: m.StoneFinalizationComponent }),
    ),

  'SS Pricing Calculator': () =>
    import('../../components/actions/pricing-calc/pricing-calc.component').then(
      (m) => ({ component: m.PricingCalcComponent }),
    ),

  'Mix Pricing Calculator': () =>
    import('../../components/actions/mix-pricing-calc/mix-pricing-calc.component').then(
      (m) => ({ component: m.MixPricingCalcComponent }),
    ),

  'Stone Tracking': () =>
    import('../../components/actions/stone-tracking/stone-tracking.component').then(
      (m) => ({ component: m.StoneTrackingComponent }),
    ),

  'Skip Assortment Master': () =>
    import('../../components/actions/skip-assortment-criteria/skip-assortment-criteria.component').then(
      (m) => ({ component: m.SkipAssortmentCriteriaComponent }),
    ),

  Sold: () =>
    import('../../components/actions/sold/sold.component').then((m) => ({
      component: m.SoldComponent,
    })),

  'Stock Upload': () =>
    import('src/app/components/actions/stock-upload/stock-upload.component').then(
      (m) => ({ component: m.StockUploadComponent }),
    ),

  'Lab Packet Series Master': () =>
    import('src/app/components/actions/lab-packet-service-master/lab-packet-service-master.component').then(
      (m) => ({ component: m.LabPacketServiceMasterComponent }),
    ),

  'Schedule Stock Export': () =>
    import('src/app/components/actions/ftp-master/ftp-master.component').then(
      (m) => ({ component: m.FtpMasterComponent }),
    ),

  'Sticker Printing': () =>
    import('src/app/components/actions/sticker-printing/sticker-printing.component').then(
      (m) => ({ component: m.StickerPrintingComponent }),
    ),

  'Sold Search': () =>
    import('../../components/actions/sold-search/sold-search.component').then(
      (m) => ({ component: m.SoldSearchComponent }),
    ),

  'Mark As Sold': () =>
    import('../../components/actions/mark-as-sold/mark-as-sold.component').then(
      (m) => ({ component: m.MarkAsSoldComponent }),
    ),

  'Polish Group Master': () =>
    import('../../components/actions/upload-recut-rules/upload-recut-rules.component').then(
      (m) => ({ component: m.UploadRecutRulesComponent }),
    ),

  'Finalize P Price': () =>
    import('../../components/actions/finalize-pprice/finalize-pprice.component').then(
      (m) => ({ component: m.FinalizePPriceComponent }),
    ),

  'Diamond Security Check': () =>
    import('../../components/actions/diamond-security-check/diamond-security-check.component').then(
      (m) => ({ component: m.DiamondSecurityCheckComponent }),
    ),

  'Party Stock Uploader': () =>
    import('../../components/actions/purchase-bulk-uploader/purchase-bulk-uploader.component').then(
      (m) => ({ component: m.PurchaseBulkUploaderComponent }),
    ),

  'Upload Lab Result For Internal Labs': () =>
    import('../../components/actions/upload-internal-lab-results/upload-internal-lab-results.component').then(
      (m) => ({ component: m.UploadInternalLabResultsComponent }),
    ),

  'Pricing History': () =>
    import('../../components/actions/pricing-history/pricing-history.component').then(
      (m) => ({ component: m.PricingHistoryComponent }),
    ),

  'Purchase Search': () =>
    import('../../components/actions/purchase-search/purchase-search.component').then(
      (m) => ({ component: m.PurchaseSearchComponent }),
    ),

  'Enquiry Master': () =>
    import('../../components/actions/enquiry-master/enquiry-master.component').then(
      (m) => ({ component: m.EnquiryMasterComponent }),
    ),

  'CVD & QC Check': () =>
    import('../../components/actions/cvd-check/cvd-check.component').then(
      (m) => ({ component: m.CvdCheckComponent }),
    ),

  'Update Available For Sale': () =>
    import('../../components/actions/update-available-for-sales/update-available-for-sales.component').then(
      (m) => ({ component: m.UpdateAvailableForSalesComponent }),
    ),

  'Purchase Inward Stones': () =>
    import('../../components/actions/purchase-consolidation-incomingstones/purchase-consolidation-incomingstones.component').then(
      (m) => ({ component: m.PurchaseConsolidationIncomingstonesComponent }),
    ),

  'Return Purchase Stone': () =>
    import('../../components/actions/purchaseconsolidation-return-stones/purchaseconsolidation-return-stones.component').then(
      (m) => ({ component: m.PurchaseconsolidationReturnStonesComponent }),
    ),

  'Update Terms & Bank Rates': () =>
    import('../../components/actions/purchase-updatetermsandbankrates/purchase-updatetermsandbankrates.component').then(
      (m) => ({ component: m.PurchaseUpdatetermsandbankratesComponent }),
    ),

  'Update Invoice No In Purchased Stones': () =>
    import('../../components/actions/purchase-update-invoice-no-and-seq-no/purchase-update-invoice-no-and-seq-no.component').then(
      (m) => ({ component: m.PurchaseUpdateInvoiceNoAndSeqNoComponent }),
    ),

  'Update Purchased Stone Parameters': () =>
    import('../../components/actions/purchaseconsolidation-updateparameterview/purchaseconsolidation-updateparameterview.component').then(
      (m) => ({
        component: m.PurchaseconsolidationUpdateparameterviewComponent,
      }),
    ),

  'Sync Data With Search': () =>
    import('../../components/actions/update-elastic-data/update-elastic-data.component').then(
      (m) => ({ component: m.UpdateElasticDataComponent }),
    ),

  'Design By Budget': () =>
    import('src/app/components/actions/designbybudget/designbybudget.component').then(
      (m) => ({ component: m.DesignbybudgetComponent }),
    ),

  'PD Purchase Stones': () =>
    import('src/app/components/actions/pd-purchase-stones/pd-purchase-stones.component').then(
      (m) => ({ component: m.PdPurchaseStonesComponent }),
    ),

  'Pricing Logs': () =>
    import('../../components/actions/pricing-logs/pricing-logs.component').then(
      (m) => ({ component: m.PricingLogsComponent }),
    ),

  'Stone Id Generation': () =>
    import('src/app/components/actions/stone-generation/stone-generation.component').then(
      (m) => ({ component: m.StoneGenerationComponent }),
    ),

  'Rapnet Stock Upload': () =>
    import('src/app/components/actions/rapnet-stock-upload/rapnet-stock-upload.component').then(
      (m) => ({ component: m.RapnetStockUploadComponent }),
    ),

  'Process Rule Config': () =>
    import('src/app/components/actions/process-rule-config/process-rule-config.component').then(
      (m) => ({ component: m.ProcessRuleConfigComponent }),
    ),

  'Last Tray Detail': () =>
    import('src/app/components/actions/last-tray/last-tray.component').then(
      (m) => ({ component: m.LastTrayComponent }),
    ),

  'Website Dashboard': () =>
    import('src/app/components/actions/website-reports/website-reports.component').then(
      (m) => ({ component: m.WebsiteReportsComponent }),
    ),

  'Manufacturing Search': () =>
    import('src/app/components/actions/manufacturing-search/manufacturing-search.component').then(
      (m) => ({ component: m.ManufacturingSearchComponent }),
    ),

  'Block For Sale': () =>
    import('src/app/components/actions/block-for-sale/block-for-sale.component').then(
      (m) => ({ component: m.BlockForSaleComponent }),
    ),

  'Live Inventory': () =>
    import('src/app/components/actions/live-inventory/live-inventory.component').then(
      (m) => ({ component: m.LiveInventoryComponent }),
    ),

  'Website Users': () =>
    import('src/app/components/actions/website-users/website-users.component').then(
      (m) => ({ component: m.WebsiteUsersComponent }),
    ),

  'Sales Comparison Report': () =>
    import('src/app/components/actions/sales-comparison-report/sales-comparison-report.component').then(
      (m) => ({ component: m.SalesComparisonReportComponent }),
    ),

  'Website Reports': () =>
    import('src/app/components/actions/website-last-signin-detail/website-last-signin-detail.component').then(
      (m) => ({ component: m.WebsiteLastSigninDetailComponent }),
    ),

  'Sales Dashboard': () =>
    import('src/app/components/actions/sales-dashboard/sales-dashboard.component').then(
      (m) => ({ component: m.SalesDashboardComponent }),
    ),

  'Sync Other Office Stock': () =>
    import('src/app/components/actions/sync-other-office-stock/sync-other-office-stock.component').then(
      (m) => ({ component: m.SyncOtherOfficeStockComponent }),
    ),

  'SVC Request List Search': () =>
    import('src/app/components/actions/svc-hold-search/svc-hold-search.component').then(
      (m) => ({ component: m.SvcHoldSearchComponent }),
    ),

  'Lab Result Upload For Other Office Stones': () =>
    import('src/app/components/actions/lab-result-upload-for-other-office/lab-result-upload-for-other-office.component').then(
      (m) => ({ component: m.LabResultUploadForOtherOfficeComponent }),
    ),

  'Ashoka TXT File': () =>
    import('src/app/components/actions/ashoka-text-file/ashoka-text-file.component').then(
      (m) => ({ component: m.AshokaTextFileComponent }),
    ),

  'Hold Report': () =>
    import('src/app/components/actions/hold-report/hold-report.component').then(
      (m) => ({ component: m.HoldReportComponent }),
    ),

  'Polish Purchase Dashboard': () =>
    import('src/app/components/actions/polish-purchase-inward-report/polish-purchase-inward-report.component').then(
      (m) => ({ component: m.PolishPurchaseInwardReportComponent }),
    ),

  'Position Report': () =>
    import('src/app/components/actions/position-report/position-report.component').then(
      (m) => ({ component: m.PositionReportComponent }),
    ),

  'Manual Sales Upload': () =>
    import('src/app/components/actions/manual-sales-upload/manual-sales-upload.component').then(
      (m) => ({ component: m.ManualSalesUploadComponent }),
    ),

  'Plan Stones and Sales Stones': () =>
    import('src/app/components/actions/planandsalesstones/planandsalesstones.component').then(
      (m) => ({ component: m.PlanandsalesstonesComponent }),
    ),

  'Logistic Approval Letter': () =>
    import('src/app/components/actions/logistics-approval/logistics-approval.component').then(
      (m) => ({ component: m.LogisticsApprovalComponent }),
    ),

  'Inward Report Search': () =>
    import('src/app/components/actions/current-parameter-search/current-parameter-search.component').then(
      (m) => ({ component: m.CurrentParameterSearchComponent }),
    ),

  'Program Rejection': () =>
    import('src/app/components/actions/program-rejection/program-rejection.component').then(
      (m) => ({ component: m.ProgramRejectionComponent }),
    ),

  'Queued Action': () =>
    import('src/app/components/actions/queued-action/queued-action.component').then(
      (m) => ({ component: m.QueuedActionComponent }),
    ),

  'Potential Buyers': () =>
    import('src/app/components/actions/potential-buyers/potential-buyers.component').then(
      (m) => ({ component: m.PotentialBuyersComponent }),
    ),

  'Stone Offer File Upload': () =>
    import('src/app/components/actions/stone-offer-capture/stone-offer-capture.component').then(
      (m) => ({ component: m.StoneOfferCaptureComponent }),
    ),

  'RFID History': () =>
    import('src/app/components/actions/rfid-history/rfid-history.component').then(
      (m) => ({ component: m.RfidHistoryComponent }),
    ),

  'Client Selection & Rejection': () =>
    import('src/app/components/actions/stone-selection-rejection/stone-selection-rejection.component').then(
      (m) => ({ component: m.StoneSelectionRejectionComponent }),
    ),

  'Stone Price Tracking': () =>
    import('src/app/components/actions/stone-price-tracking/stone-price-tracking.component').then(
      (m) => ({ component: m.StonePriceTrackingComponent }),
    ),

  'Scan Via RFID': () =>
    import('src/app/components/actions/scan-via-rfid/scan-via-rfid.component').then(
      (m) => ({ component: m.ScanViaRFIDComponent }),
    ),

  'Global SVC Search': () =>
    import('src/app/components/actions/global-svcsearch/global-svcsearch.component').then(
      (m) => ({ component: m.GlobalSVCSearchComponent }),
    ),

  'Intransit Stone Price Upload': () =>
    import('src/app/components/actions/upload-manual-pricing/upload-manual-pricing.component').then(
      (m) => ({ component: m.UploadManualPricingComponent }),
    ),

  Bulletin: () =>
    import('src/app/components/actions/bulletin/bulletin.component').then(
      (m) => ({ component: m.BulletinComponent }),
    ),

  'Website Users Notifications': () =>
    import('src/app/components/actions/websiteusernotifications/websiteusernotifications.component').then(
      (m) => ({ component: m.WebsiteusernotificationsComponent }),
    ),

  Layouts: () =>
    import('src/app/components/actions/layouts/layouts.component').then(
      (m) => ({ component: m.LayoutsComponent }),
    ),

  'Upload PDcat': () =>
    import('src/app/components/actions/upload-pdcat/upload-pdcat.component').then(
      (m) => ({ component: m.UploadPDcatComponent }),
    ),

  'Upload PD Parameter': () =>
    import('src/app/components/update-pdfile-upload/update-pdfile-upload.component').then(
      (m) => ({ component: m.UpdatePDFileUploadComponent }),
    ),

  'Website Config': () =>
    import('src/app/components/actions/website-config/website-config.component').then(
      (m) => ({ component: m.WebsiteConfigComponent }),
    ),

  'Pricing Trigger Report': () =>
    import('src/app/components/actions/pricing-trigger-report/pricing-trigger-report.component').then(
      (m) => ({ component: m.PricingTriggerReportComponent }),
    ),

  'Manufacturing Polish Group Master': () =>
    import('src/app/components/actions/polish-group-master-for-trybe/polish-group-master-for-trybe.component').then(
      (m) => ({ component: m.PolishGroupMasterForTrybeComponent }),
    ),

  'Pricing Change Request List': () =>
    import('src/app/components/actions/pricing-change-request/pricing-change-request.component').then(
      (m) => ({ component: m.PricingChangeRequestComponent }),
    ),

  'Update Scanner Board Url': () =>
    import('src/app/components/update-my-scanner-board/update-my-scanner-board.component').then(
      (m) => ({ component: m.UpdateMyScannerBoardComponent }),
    ),

  'Other Office Stock': () =>
    import('src/app/components/other-office-stock-process/other-office-stock-process.component').then(
      (m) => ({ component: m.OtherOfficeStockProcessComponent }),
    ),

  'Batch Party Stock Uploader': () =>
    import('src/app/components/batchpartystockuploader/batchpartystockuploader.component').then(
      (m) => ({ component: m.BatchpartystockuploaderComponent }),
    ),

  'Third party Request Config': () =>
    import('src/app/components/actions/third-party-request-config/third-party-request-config.component').then(
      (m) => ({ component: m.ThirdPartyRequestConfigComponent }),
    ),

  'GIA Execution Process': () =>
    import('src/app/components/actions/gia-execution-process/gia-execution-process.component').then(
      (m) => ({ component: m.GiaExecutionProcessComponent }),
    ),

  'Memo Return Stones': () =>
    import('src/app/components/actions/return-memo-stone/return-memo-stone.component').then(
      (m) => ({ component: m.ReturnMemoStoneComponent }),
    ),

  'Sync Stones': () =>
    import('src/app/components/actions/sync-stones/sync-stones.component').then(
      (m) => ({ component: m.SyncStonesComponent }),
    ),

  'Memo Mix Stone Tracking': () =>
    import('src/app/components/actions/memo-mix-stone-tracking/memo-mix-stone-tracking.component').then(
      (m) => ({ component: m.MemoMixStoneTrackingComponent }),
    ),
};
